import React from 'react';
import ErrorsComponent from 'shared/Errors/components/Errors';
import { addTestingIdentifier } from 'utils/testing';
import { isSafari } from 'shared/Browser';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es, enUS } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import Input, { InputProps } from '../elements/Input';
import BaseGroupComponent, { BaseGroupProps } from './BaseGroup';

// PLA-1057: locales para que el date-picker muestre el mes por nombre (Agosto 2026 / August 2026)
// según el idioma del usuario, en vez de '2026-08'.
registerLocale('es', es);
registerLocale('en', enUS);

export interface InputGroupProps extends BaseGroupProps, InputProps {
  errors?: string[];
  // PLA-1057: fuerza el date-picker (react-datepicker) para inputs type='month' en TODOS los
  // navegadores, no solo Safari, evitando el <input type="month"> nativo (que se congela al teclear
  // el año) y habilitando elegir el año desde el picker.
  monthYearPicker?: boolean;
}

const InputGroupComponent = React.forwardRef<HTMLInputElement, InputGroupProps>(
  (
    {
      label,
      labelAddon,
      append,
      prepend,
      className,
      info,
      infoname,
      condensed,
      inline,
      noMargin,
      changeIndicated,
      errors,
      required,
      monthYearPicker,
      ...rest
    },
    ref
  ) => {
    const inputComponent = () => {
      if (rest.type === 'month' && (isSafari() || monthYearPicker)) {
        const parsedMinDate =
          typeof rest.min === 'string' ? new Date(`${rest.min}-02`) : rest.min;
        // El input visible del picker NO lleva `name`: muestra el mes por nombre (locale) pero no
        // debe enviarse ese texto. El valor real ('YYYY-MM') viaja en un input hidden para el backend.
        const { name, ...datePickerRest } = rest;
        const rawValue = rest.value as unknown;
        const selectedDate = rawValue instanceof Date ? rawValue : undefined;
        const submitValue = selectedDate
          ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(
              2,
              '0'
            )}`
          : '';
        const locale = (I18n as any)?.locale === 'es' ? 'es' : 'en';

        return (
          <>
            <DatePicker
              showMonthYearPicker
              dateFormat='MMMM yyyy'
              locale={locale}
              minDate={parsedMinDate}
              selected={selectedDate}
              // PLA-1057: solo selección desde el picker; se bloquea la escritura manual en el campo.
              onChangeRaw={(event) => event?.preventDefault()}
              onChange={(date) => {
                rest.onChange && rest.onChange(date);
              }}
              {...datePickerRest}
            />
            <input type='hidden' name={name} value={submitValue} readOnly />
          </>
        );
      }

      return (
        <Input
          ref={ref}
          condensed={condensed}
          changeIndicated={changeIndicated}
          required={required}
          {...addTestingIdentifier(rest.dataCy || rest.name)}
          {...rest}
        />
      );
    };

    return (
      <BaseGroupComponent
        label={label}
        labelAddon={labelAddon}
        append={append}
        prepend={prepend}
        className={className}
        info={info}
        infoname={infoname}
        condensed={condensed}
        inline={inline}
        noMargin={noMargin}
        required={required}
        changeIndicated={changeIndicated}
        errors={errors && <ErrorsComponent errors={errors} />}
      >
        {inputComponent()}
      </BaseGroupComponent>
    );
  }
);

InputGroupComponent.displayName = 'InputGroupComponent';

export default InputGroupComponent;
